import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type {
  Credential,
  CredentialDocument,
  CredentialVerification,
} from "../../types";
import { blankCredentialInstance } from "../../lib/blank-credential-instance";
import { CredentialRow } from "./credential-row";

/**
 * All of a provider's instances of one credential type, under one heading.
 *
 * A provider licensed in four states used to get four cards each headed "State
 * License"; the type name is the least informative thing on the row when it is
 * repeated. It is written once here, and each instance below leads with what
 * distinguishes it — the state, the board, the employer.
 *
 * Single-instance types render through the same block so the page has one
 * layout rather than two that drift.
 */
export function CredentialTypeBlock({
  credentials,
  userId,
  readOnly = false,
  onEditCredential,
  onOpenDocument,
  onOpenVerificationEvidence,
  isOpeningDocument,
  onRemoveDocument,
  appearance = "default",
}: {
  /** Every instance of one type, in display order. Never empty. */
  credentials: Credential[];
  userId: string;
  appearance?: "default" | "panel";
  readOnly?: boolean;
  onEditCredential?: (credential: Credential) => void;
  onOpenDocument?: (document: CredentialDocument) => void;
  onOpenVerificationEvidence?: (verification: CredentialVerification) => void;
  isOpeningDocument?: boolean;
  onRemoveDocument?: (input: {
    credential: Credential;
    document: CredentialDocument;
  }) => void;
}) {
  const [first] = credentials;
  const held = credentials.filter((credential) => credential.present);

  /**
   * "Add another" belongs to the type, not to any one instance, so it sits in
   * the header. Only for types that may be held more than once, and only once
   * at least one exists — otherwise the empty instance's own Add is the way in.
   */
  const canAddAnother =
    !readOnly && !!onEditCredential && first.allowsMultiple && held.length > 0;

  return (
    <div className="py-4 first:pt-2">
      <div className="flex items-center justify-between gap-2">
        <h4
          className={cn(
            "text-xs font-semibold uppercase tracking-wide text-slate-500",
            appearance === "panel" &&
              "text-sm normal-case tracking-normal text-slate-800",
          )}
        >
          {first.label}
          {first.requirement === "optional" && (
            <span
              className={cn(
                "ml-2 text-xs font-normal normal-case tracking-normal text-slate-400",
                appearance === "panel" && "text-slate-500",
              )}
            >
              Optional
            </span>
          )}
        </h4>
        {canAddAnother && (
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              "h-8 gap-1.5 rounded-xl px-2 text-xs font-medium text-primary hover:bg-primary/5 hover:text-primary",
              appearance === "panel" &&
                "h-9 shrink-0 border border-slate-300 bg-white",
            )}
            onClick={() =>
              onEditCredential?.(blankCredentialInstance(first, "new"))
            }
          >
            <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            Add another
          </Button>
        )}
      </div>

      <div className="mt-2 space-y-2">
        {credentials.map((credential) => (
          <CredentialRow
            key={credential.id}
            credential={credential}
            appearance={appearance}
            userId={userId}
            readOnly={readOnly}
            onEdit={onEditCredential}
            onOpenDocument={onOpenDocument}
            onOpenVerificationEvidence={onOpenVerificationEvidence}
            isOpeningDocument={isOpeningDocument}
            onRemoveDocument={onRemoveDocument}
          />
        ))}
      </div>
    </div>
  );
}
